import asyncErrorHandler, { IErrorHandlerResponse } from 'utils/requests/errorHandler';
import request from './request.service';
import { apiUrls } from 'constants/urls';
import { Roles } from 'types/auth';
import { IAdminType } from 'types/admins';
import filterEmptyParams from 'utils/common/filterEmptyParams';

type GetMyProfileResponse = IErrorHandlerResponse<IAdminType>;

export const getMyProfile = async (): Promise<GetMyProfileResponse> => {
  const callback = async () => {
    return await request(apiUrls.auth.currentUser);
  };

  return await asyncErrorHandler(callback);
};

interface UpdatePasswordParams {
  password: string;
  newPassword: string;
  totp?: string;
}

type UpdatePasswordResponse = IErrorHandlerResponse<null>;

export const updatePassword = async ({
  password,
  newPassword,
  totp,
}: UpdatePasswordParams): Promise<UpdatePasswordResponse> => {
  const callback = async () => {
    return await request(apiUrls.auth.changePassword, {
      method: 'POST',
      body: filterEmptyParams({
        password,
        new_password: newPassword,
        otp_2fa: totp,
      }),
    });
  };

  return await asyncErrorHandler(callback);
};

interface DisableTwoFaParams {
  otp?: string;
  totp?: string;
}

type DisableTwoFaResponse = IErrorHandlerResponse<null>;

export const disable2Fa = async ({ totp, otp }: DisableTwoFaParams): Promise<DisableTwoFaResponse> => {
  const callback = async () => {
    return await request(apiUrls.auth.disableTwoFa, {
      method: 'POST',
      body: filterEmptyParams({ totp, otp }),
    });
  };

  return await asyncErrorHandler(callback);
};

interface EnableTwoFaParams {
  otp?: string;
  totp?: string;
}

type EnableTwoFaResponse = IErrorHandlerResponse<{ recovery_tokens: string[] }>;

export const enable2Fa = async ({ totp }: EnableTwoFaParams): Promise<EnableTwoFaResponse> => {
  const callback = async () => {
    return await request(apiUrls.auth.twoFa, {
      method: 'POST',
      body: { totp },
    });
  };

  return await asyncErrorHandler(callback);
};

type GetTwoFaUrlResponse = IErrorHandlerResponse<{ totpUri: string }>;

export const get2FaUrl = async (): Promise<GetTwoFaUrlResponse> => {
  const callback = async () => {
    return await request(apiUrls.auth.twoFa);
  };

  return await asyncErrorHandler(callback);
};

type GetAdminsResponse = IErrorHandlerResponse<{ data: IAdminType[] }>;

export const getAdmins = async (): Promise<GetAdminsResponse> => {
  const callback = async () => {
    return await request(apiUrls.admin.all);
  };

  return await asyncErrorHandler(callback);
};

interface CreateAdminParams {
  email: string;
  username: string;
  roles: Roles[];
}

type CreateAdminResponse = IErrorHandlerResponse<null>;

export const inviteAdmin = async (body: CreateAdminParams): Promise<CreateAdminResponse> => {
  const roles = body.roles.join(',');

  const callback = async () => {
    return await request(apiUrls.admin.invite, {
      method: 'POST',
      body: { ...body, roles },
    });
  };

  return await asyncErrorHandler(callback);
};
